import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isArray from 'lodash/isArray';
import { defineStore, acceptHMRUpdate } from 'pinia';
import {
    createReport,
    deleteReport,
    fetchReport,
    fetchReports,
    minifyReports,
    updateReport,
    mapSegmentData,
} from '@/vuex/modules/saved-reports/saved-reports.utils';
import { getReportType, isDataExplorerReport, isFunnelReport } from '@/reports/utils';
import { removeReportMapping } from '@/integrations/sf/report.utils';
import migrateReportSchema from '@/vuex/modules/data-explorer/migration';
import { HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants.js';
import { useHistoryStore } from '@/stores/history.store';
import { useWorkflowsStore } from '@/stores/workflows.store';
import { sendReportObjectMetadata } from '@/utils/pendo-object-metadata';

interface SavedReport {
    id: string;
    name: string;
    description: string;
    createdByUser: { username: string };
    type: string;
    appId: string;
    shared: boolean;
    rootVersionId: string;
    stableVersionId: string;
}

interface SavedReportsState {
    activeReportId: string | undefined;
    map: Record<string, SavedReport>;
    error: unknown;
    fetching: boolean;
}

interface HistoryItem {
    id: string;
    reportName: string;
    description: string;
    createdBy: string;
    historyType: string;
    routeName: string;
    label: string;
    timestamp: number;
}

let loadAllPromise: Promise<{ data: SavedReport[] }> | null = null;

export const useSavedReportsStore = defineStore('savedReports', {
    state: (): SavedReportsState => ({
        activeReportId: undefined,
        map: {},
        error: null,
        fetching: false,
    }),

    getters: {
        activeReport (state: SavedReportsState): SavedReport | Record<string, never> {
            return state.map[state.activeReportId!] || {};
        },
        list (state: SavedReportsState): SavedReport[] {
            return values(state.map);
        },
        listByType (): (filterType: string | string[]) => SavedReport[] {
            return (filterType) =>
                filter(this.list, (report) => {
                    const reportType = getReportType(report);

                    return isArray(filterType) ? filterType.includes(reportType) : filterType === reportType;
                });
        },
        reportById (state: SavedReportsState): (id: string) => SavedReport | undefined {
            return (id) => state.map[id];
        },
        funnelHistory (state: SavedReportsState): HistoryItem[] {
            const history: Array<{ id: string; timestamp: number }> =
                useHistoryStore().itemHistory(HISTORY_TYPES.FUNNEL);

            return history
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => ({
                    id,
                    reportName: state.map[id].name,
                    description: state.map[id].description,
                    createdBy: state.map[id].createdByUser.username,
                    historyType: HISTORY_TYPES.FUNNEL,
                    routeName: 'FunnelDetails',
                    label: 'Funnel',
                    timestamp,
                }));
        },
        aiAgentHistory (state: SavedReportsState): HistoryItem[] {
            const history: Array<{ id: string; timestamp: number }> =
                useHistoryStore().itemHistory(HISTORY_TYPES.AI_AGENT);

            return history
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => ({
                    id,
                    reportName: state.map[id].name,
                    description: state.map[id].description,
                    createdBy: state.map[id].createdByUser.username,
                    historyType: HISTORY_TYPES.AI_AGENT,
                    routeName: 'AiAgentReport',
                    label: 'AI Agent',
                    timestamp,
                }));
        },
        pathHistory (state: SavedReportsState): HistoryItem[] {
            const history: Array<{ id: string; timestamp: number }> =
                useHistoryStore().itemHistory(HISTORY_TYPES.PATH);

            return history
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => ({
                    id,
                    reportName: state.map[id].name,
                    description: state.map[id].description,
                    createdBy: state.map[id].createdByUser.username,
                    historyType: HISTORY_TYPES.PATH,
                    routeName: 'PathDetails',
                    label: 'Path',
                    timestamp,
                }));
        },
        retentionHistory (state: SavedReportsState): HistoryItem[] {
            const history: Array<{ id: string; timestamp: number }> =
                useHistoryStore().itemHistory(HISTORY_TYPES.RETENTION);

            return history
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => ({
                    id,
                    reportName: state.map[id].name,
                    description: state.map[id].description,
                    createdBy: state.map[id].createdByUser.username,
                    historyType: HISTORY_TYPES.RETENTION,
                    routeName: 'RetentionDetails',
                    label: 'Retention',
                    timestamp,
                }));
        },
        dataExplorerHistory (state: SavedReportsState): HistoryItem[] {
            const history: Array<{ id: string; timestamp: number }> =
                useHistoryStore().itemHistory(HISTORY_TYPES.DATA_EXPLORER) ?? [];

            return history
                .filter(({ id }) => !!state.map[id])
                .map(({ id, timestamp }) => ({
                    id,
                    reportName: state.map[id].name,
                    description: state.map[id].description,
                    createdBy: state.map[id].createdByUser.username,
                    historyType: HISTORY_TYPES.DATA_EXPLORER,
                    routeName: 'DataExplorer',
                    label: 'Data Explorer',
                    timestamp,
                }));
        },
        allReportHistory (): HistoryItem[] {
            const workflowHistory = (useWorkflowsStore().workflowHistory ?? []) as unknown as HistoryItem[];
            const reports = [
                ...this.funnelHistory,
                ...this.pathHistory,
                ...this.retentionHistory,
                ...this.dataExplorerHistory,
            ];

            return [...reports, ...workflowHistory].sort((prev, next) => next.timestamp - prev.timestamp);
        },
    },

    actions: {
        setActiveReport ({ report }: { report: { id: string | undefined } }): void {
            this.activeReportId = report.id;
        },
        deleteMapKey ({ id }: { id: string }): void {
            delete this.map[id];
        },
        removeReportsForAgentId ({ agentId }: { agentId: string }): void {
            Object.keys(this.map).forEach((id) => {
                const reportAiAgentId = (this.map[id] as { definition?: { aiAgentId?: string } }).definition?.aiAgentId;
                if (reportAiAgentId === agentId) {
                    delete this.map[id];
                    if (this.activeReportId === id) {
                        this.activeReportId = undefined;
                    }
                }
            });
        },
        setError ({ error }: { error: unknown }): void {
            this.error = error;
        },
        setFetching ({ fetching }: { fetching: boolean }): void {
            this.fetching = fetching;
        },
        setMap ({ map }: { map: Record<string, SavedReport> }): void {
            this.map = map;
        },
        setMapAtKey ({ id, report }: { id: string; report: SavedReport }): void {
            this.map[id] = report;
        },
        clearActiveReport (): void {
            this.activeReportId = undefined;
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}): Promise<void> {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }
            this.fetching = true;
            loadAllPromise = fetchReports();
            try {
                const { data: reports } = await loadAllPromise;
                const minifiedReports = minifyReports(Array.isArray(reports) ? reports : []);
                const filteredReports = minifiedReports?.filter?.(({ type }: { type: string }) => type !== 'Trends');
                const reportMap = keyBy(filteredReports, 'id');
                if (this.activeReportId && this.map[this.activeReportId] && reportMap[this.activeReportId]) {
                    reportMap[this.activeReportId] = this.map[this.activeReportId];
                }
                this.$patch({ fetching: false, map: reportMap });
            } catch (error) {
                this.fetching = false;
                this.error = error;
                throw error;
            } finally {
                loadAllPromise = null;
            }
        },
        async loadAllOfType ({ type }: { type: string }): Promise<void> {
            this.fetching = true;
            try {
                const { data: reports } = await (fetchReports as (type?: string | null) => Promise<{ data: SavedReport[] }>)(type);
                const minifiedReports = minifyReports(Array.isArray(reports) ? reports : []);
                const reportMap = keyBy(minifiedReports, 'id');
                if (this.activeReportId && this.map[this.activeReportId] && reportMap[this.activeReportId]) {
                    reportMap[this.activeReportId] = this.map[this.activeReportId];
                }
                this.$patch({ fetching: false, map: reportMap });
            } catch (error) {
                this.fetching = false;
                this.error = error;
                throw error;
            }
        },
        async loadOne ({ reFetch = true, id }: { reFetch?: boolean; id: string }): Promise<void> {
            if (!reFetch && !!this.reportById(id)) {
                this.activeReportId = id;

                return Promise.resolve();
            }
            this.fetching = true;
            try {
                let { data: report } = await fetchReport(id);
                if (isDataExplorerReport(report)) {
                    report = migrateReportSchema(report);
                }
                mapSegmentData(report, report);
                this.$patch({ fetching: false });
                this.map[report.id] = report;
                this.activeReportId = report.id;
                sendReportObjectMetadata(report);
            } catch (error) {
                this.$patch((state) => { state.fetching = false; state.error = error; });
                throw error;
            }
        },
        async create ({ report }: { report: Partial<SavedReport> }): Promise<SavedReport> {
            this.fetching = true;
            try {
                const { data: newReport } = await createReport(report);
                const queryParams = new URLSearchParams(window.location.search);
                const globalCreate = queryParams.has('globalCreate');
                window.pendo?.track('report-created', {
                    type: newReport.type === 'Saved Reports' ? 'Data Explorer' : newReport.type,
                    ...(globalCreate && { location: 'globalCreate' }),
                });
                mapSegmentData(newReport, newReport);
                this.$patch({ fetching: false });
                this.map[newReport.id] = newReport;
                this.activeReportId = newReport.id;

                return newReport;
            } catch (error) {
                this.$patch((state) => { state.fetching = false; state.error = error; });
                throw error;
            }
        },
        async update ({ report, skipVersioning = false }: { report: SavedReport; skipVersioning?: boolean }): Promise<SavedReport> {
            this.fetching = true;
            let effectiveSkipVersioning = skipVersioning;
            if (!isFunnelReport(report)) {
                effectiveSkipVersioning = false;
            }
            try {
                const { data: newReport } = await updateReport(report, effectiveSkipVersioning);
                mapSegmentData(newReport, newReport);
                this.$patch({ fetching: false });
                this.map[newReport.id] = newReport;
                this.activeReportId = newReport.id;

                return newReport;
            } catch (error) {
                this.$patch((state) => { state.fetching = false; state.error = error; });
                throw error;
            }
        },
        async remove (report: Pick<SavedReport, 'id'>): Promise<void> {
            const { id } = report;
            this.fetching = true;
            try {
                await deleteReport(id);
                this.$patch({ fetching: false });
                delete this.map[id];
                if (this.activeReportId === id) {
                    this.activeReportId = undefined;
                }
            } catch (error) {
                this.$patch((state) => { state.fetching = false; state.error = error; });
                throw error;
            }
            try {
                await removeReportMapping(report);
            } catch {
                // Report is already deleted; stale mapping clears on next refresh.
            }
        },
    },
});

export function __resetLoadAllPromiseForUnitTests (): void {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSavedReportsStore, module.hot));
}
